import { useState, useEffect } from "react";
import type { Project, MemoryItem } from "@cowork/shared";

const PROJECTS_KEY = "cowork:projects";
const ACTIVE_PROJECT_KEY = "cowork:activeProjectId";

export const PROJECT_COLORS = [
  "#d97757", // orange (default)
  "#7c6fcd", // purple
  "#4a9d8f", // teal
  "#c17a4a", // amber
  "#a85c7a", // rose
  "#5a7ec4", // blue
];

function load(): Project[] {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    return raw ? (JSON.parse(raw) as Project[]) : [];
  } catch {
    return [];
  }
}

function save(projects: Project[]): void {
  try {
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
  } catch {
    // quota exceeded
  }
}

export function useProjectStore() {
  const [projects, setProjects] = useState<Project[]>(() => load());
  const [activeProjectId, setActiveProjectId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_PROJECT_KEY),
  );

  useEffect(() => { save(projects); }, [projects]);
  useEffect(() => {
    if (activeProjectId) localStorage.setItem(ACTIVE_PROJECT_KEY, activeProjectId);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY);
  }, [activeProjectId]);

  const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;

  const createProject = (title: string): Project => {
    const now = new Date().toISOString();
    const project: Project = {
      id: crypto.randomUUID(),
      title,
      description: "",
      instructions: "",
      color: PROJECT_COLORS[projects.length % PROJECT_COLORS.length]!,
      pinnedSkills: [],
      memory: [],
      createdAt: now,
      updatedAt: now,
    };
    setProjects((prev) => [...prev, project]);
    return project;
  };

  const updateProject = (
    id: string,
    patch: Partial<Pick<Project, "title" | "description" | "instructions" | "color" | "pinnedSkills">>,
  ) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, ...patch, updatedAt: new Date().toISOString() } : p,
      ),
    );
  };

  const deleteProject = (id: string) => {
    setProjects((prev) => prev.filter((p) => p.id !== id));
    if (activeProjectId === id) setActiveProjectId(null);
  };

  const addMemory = (projectId: string, content: string) => {
    const item: MemoryItem = {
      id: crypto.randomUUID(),
      content,
      createdAt: new Date().toISOString(),
    };
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, memory: [...p.memory, item], updatedAt: new Date().toISOString() }
          : p,
      ),
    );
  };

  const updateMemory = (projectId: string, itemId: string, content: string) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              memory: p.memory.map((m) => (m.id === itemId ? { ...m, content } : m)),
              updatedAt: new Date().toISOString(),
            }
          : p,
      ),
    );
  };

  const removeMemory = (projectId: string, itemId: string) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, memory: p.memory.filter((m) => m.id !== itemId), updatedAt: new Date().toISOString() }
          : p,
      ),
    );
  };

  return {
    projects,
    activeProject,
    activeProjectId,
    setActiveProjectId,
    createProject,
    updateProject,
    deleteProject,
    addMemory,
    updateMemory,
    removeMemory,
  };
}
